<template>
  <div class="group-selector-wrapper">
    <div class="selector-trigger" @click="showModal = true">
      <span class="active-group-name">{{ modelValue ? modelValue.original_name || modelValue.name : 'Gruppe wählen...' }}</span>
      <div class="switch-button">
        <span class="switch-text"><img src="@/assets/images/back.svg"></span>
        <div v-if="totalUnread > 0" class="unread-badge inline">{{ totalUnread }}</div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <Teleport to="body">
      <div v-if="showModal" class="darken-page" @click="showModal = false">
        <div class="group-modal card" @click.stop>
          <div class="modal-header">
            <h3>Deine Gruppen</h3>
            <button class="close-btn" @click="showModal = false">
              <img src="@/assets/images/cross.svg" alt="Schließen">
            </button>
          </div>
          <div class="group-list">
            <div 
              v-for="pool in pools" 
              :key="pool.id" 
              class="group-item" 
              :class="{ active: modelValue && modelValue.id === pool.id }"
              @click="selectPool(pool)"
            >
              <div class="group-item-info">
                <span class="pool-name">{{ pool.original_name || pool.name }}</span>
                <span v-if="pool.unreadMessages > 0" class="unread-badge">{{ pool.unreadMessages }}</span>
              </div>
              <div v-if="modelValue && modelValue.id === pool.id" class="active-indicator">
                <div class="dot"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'GroupSelector',
  props: {
    modelValue: {
      type: Object,
      default: null
    },
    pools: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      showModal: false
    }
  },
  computed: {
    totalUnread() {
      if (!this.pools) return 0;
      return this.pools.reduce((sum, pool) => {
        if (this.modelValue && pool.id === this.modelValue.id) return sum;
        return sum + (pool.unreadMessages || 0);
      }, 0);
    }
  },
  methods: {
    selectPool(pool) {
      this.$emit('update:modelValue', pool);
      this.$emit('change', pool);
      this.showModal = false;
    }
  }
}
</script>

<style scoped>
.group-selector-wrapper {
  width: 100%;
  max-width: 250px;
  margin: 10px auto 20px;
}

.selector-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 6px 6px 6px 20px;
  border-radius: var(--radius-xl);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-soft);
  cursor: pointer;
  transition: all 0.2s ease;
}

.selector-trigger:active {
  transform: scale(0.98);
}

.active-group-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-on-gradient);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  text-align: left;
}

.switch-button {
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: var(--radius-xl);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.selector-trigger:hover .switch-button {
  background: rgba(255, 255, 255, 0.3);
}

.switch-text {
  font-size: 0.85rem;
  font-weight: 800;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.switch-text img {
  transform: rotate(-90deg) translateX(4px);
  filter: invert(1);
}

.unread-badge {
  background: var(--primary-color);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 800;
  min-width: 20px;
  text-align: center;
}

.unread-badge.inline {
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
}

/* Modal Styles */
.group-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  padding: 0; /* Override card padding */
  overflow: hidden;
  animation: modalScaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalScaleUp {
  from { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
  to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}

.modal-header {
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #1D1D1F;
}

.close-btn {
  background: #F5F5F7;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: pointer;
}

.close-btn img {
  width: 100%;
  opacity: 0.5;
}

.group-list {
  padding: 12px;
  overflow-y: auto;
  flex: 1;
}

.group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  margin-bottom: 8px;
  border-radius: var(--radius-m);
  cursor: pointer;
  transition: background 0.2s ease;
}

.group-item:hover {
  background: rgba(0, 0, 0, 0.03);
}

.group-item.active {
  background: rgba(0, 122, 255, 0.08);
}

.group-item-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pool-name {
  font-weight: 600;
  font-size: 1.05rem;
  color: #1D1D1F;
}

.active-indicator .dot {
  width: 8px;
  height: 8px;
  background: var(--primary-color);
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 122, 255, 0.4);
}
</style>
